import { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { VariantExtended } from '@scale/scaleui/dist/v1/types';
import { AnnotationAttributeType, GeometryKey, ComposableKey, TaskType } from '@scale/shared-types';
import { PartialRecord } from '@scale/shared/types/utils';
import { twMerge } from 'tailwind-merge';

export const PROJECTED_CUBOID_TOOL_NAMES: Record<string, string> = {
  box_2d: 'Camera',
  polygon_2d: 'Camera',
  polygon_topdown: 'Topdown',
};

export const GEOMETRY_TO_ICON: Partial<Record<ComposableKey, IconProp>> = {
  cuboid: ['far', 'cube'],
  point_segmentation: ['far', 'puzzle-piece'],
  plane: ['far', 'map'],
  mesh: ['far', 'border-none'],
  box_2d: ['far', 'square'],
  point_2d: ['far', 'map-marker-alt'],
  polyline_2d: 'slash',
  polygon_2d: ['far', 'vector-square'],
  event_2d: ['far', 'clock'],
  point_3d: ['far', 'map-marker-alt'],
  polyline_3d: ['far', 'slash'],
  point_topdown: ['far', 'map-marker-alt'],
  polygon_topdown: ['far', 'vector-square'],
  polygon_3d: ['far', 'vector-square'],
  image_segmentation: ['far', 'puzzle-piece'],
  cuboid_2d: ['far', 'cube'],
  group: ['far', 'layer-group'],
  text_annotation: ['far', 'font'],
  links: ['far', 'link'],
  scene: ['far', 'border-all'],
  lidar_linking: ['far', 'project-diagram'],
  grid_segmentation: ['far', 'th'],
  sensorfusion_calibrate: ['far', 'crosshairs'],
};

export const ATTR_TYPE_TO_ICON: Record<string, IconProp> = {
  [AnnotationAttributeType.Category]: ['far', 'list'],
  [AnnotationAttributeType.Linked]: ['far', 'link'],
  [AnnotationAttributeType.Angle]: ['far', 'ruler-triangle'],
  [AnnotationAttributeType.Number]: ['far', 'sliders-h'],
  [AnnotationAttributeType.Text]: ['far', 'text'],
};

export const GEOMETRY_TO_IMAGE_URL: Record<string, string> = {
  cuboid: 'https://static.scale.com/taxonomy_service_assets/cuboids.png',
  box_2d: 'https://static.scale.com/taxonomy_service_assets/box_2d.png',
  polygon_2d: 'https://static.scale.com/taxonomy_service_assets/polygon_2d.png',
  point_topdown: 'https://static.scale.com/taxonomy_service_assets/topdown_points.png',
  polyline_3d: 'https://static.scale.com/taxonomy_service_assets/topdown_polylines.png',
  polygon_topdown: 'https://static.scale.com/taxonomy_service_assets/topdown_polygons.png',
  polyline_2d: 'https://static.scale.com/taxonomy_service_assets/points_lines_2d.png',
  point_2d: 'https://static.scale.com/taxonomy_service_assets/points_lines_2d.png',
  cuboid_2d: 'https://static.scale.com/taxonomy_service_assets/cuboid_2d.png',
};

export interface ActionMenuFuncs {
  handleStartCreateFirstTaxonomyFromTask?: (version?: number) => void;
  handleStartCreateFirstTaxonomy?: (version?: number) => void;
  handleStartWithMasterTaxonomy?: (version?: number) => void;
  handleStartCreateSubtaxonomy?: (version: number) => void;
  handleStartPublish?: (version: number) => void;
  handleStartInherit?: (taxonomyId: string) => void;
  handleStartFork?: (taxonomyId: string) => void;
  handleStartCommit?: (taxonomyId: string) => void;
  handleStartRecall?: (taxonomyId: string) => void;
  handleStartBackfillTasks?: (taxonomyId: string) => void;
  handleStartCreateInstructions?: () => void;
  handleRequestFEApproval?: (version: number) => void;
  handleStartMultipleCommits?: (taxonomyIds: string[]) => void;
}

export const ACTION_DISPLAY: Record<
  keyof ActionMenuFuncs,
  { title: string; subtitle: string; icon: IconProp; variant: VariantExtended }
> = {
  handleStartCreateFirstTaxonomyFromTask: {
    title: 'Use Taxonomy Service for this project',
    subtitle: 'Import Taxonomy from Task',
    icon: 'play-circle',
    variant: 'primary',
  },
  handleStartCreateFirstTaxonomy: {
    title: 'Use Taxonomy Service for this project',
    subtitle: 'Start with empty Taxonomy',
    icon: 'play-circle',
    variant: 'secondary',
  },
  handleStartWithMasterTaxonomy: {
    title: 'Use the master taxonomy as a base',
    subtitle: 'Start with master taxonomy',
    icon: 'play-circle',
    variant: 'black',
  },
  handleStartCreateSubtaxonomy: {
    title: 'Define another portion of the taxonomy under the root version',
    subtitle: 'Create subtaxonomy',
    icon: 'code-branch',
    variant: 'primary',
  },
  handleStartPublish: {
    title: 'Submit to Scale. You can create Tasks after submitting.',
    subtitle: 'Submit',
    icon: 'upload',
    variant: 'primary',
  },
  handleStartInherit: {
    title: 'Copy taxonomies from older versions',
    subtitle: 'Inherit',
    icon: 'download',
    variant: 'white',
  },
  handleStartFork: {
    title: 'aka "Fork" this taxonomy',
    subtitle: 'Create substage taxonomy',
    icon: 'code-branch',
    variant: 'primary',
  },
  handleStartCommit: {
    title: 'Lock ops taxonomies for this version',
    subtitle: 'Commit',
    icon: 'lock',
    variant: 'primary',
  },
  handleStartRecall: {
    title: 'Uncommit / Make this taxonomy editable again',
    subtitle: 'Recall',
    icon: 'undo',
    variant: 'white',
  },
  handleStartBackfillTasks: {
    title: 'Backfill tasks with this taxonomy version',
    subtitle: 'Backfill tasks',
    icon: 'pen',
    variant: 'primary',
  },
  handleStartCreateInstructions: {
    title: 'Create new instructions for this taxonomy version.',
    subtitle: 'Create instructions',
    icon: 'page4',
    variant: 'primary',
  },
  handleRequestFEApproval: {
    title: 'New taxonomies need Field Engineering team approval on this project',
    subtitle: 'Request approval',
    icon: 'upload',
    variant: 'primary',
  },
  handleStartMultipleCommits: {
    title: 'Commit all the subtaxonomies under this root taxonomy',
    subtitle: 'Commit multiple',
    icon: 'lock',
    variant: 'primary',
  },
};

export interface SidebarTag {
  label: string;
  className: string;
  tooltip: string;
}
const EXTERNAL_TAG: SidebarTag = {
  label: 'cust',
  className: 'bg-neutral-100',
  tooltip: 'Customer taxonomy',
};
const EXTERNAL_PUBLISHED_TAG: SidebarTag = {
  label: 'submitted',
  className: 'bg-success-200',
  tooltip:
    'Taxonomy is submitted to Scale for review. You can now submit Tasks with this Taxonomy.',
};
export const LATEST_TAX_TAG: SidebarTag = {
  label: 'last published',
  className: 'bg-success-200',
  tooltip: 'New Tasks will use this Taxonomy',
};
export const LATEST_INSTR_TAG: SidebarTag = {
  label: 'last published',
  className: 'bg-info-100',
  tooltip: 'New Tasks will use this Instructions version',
};

const SUB_COMMITTED_TAG: SidebarTag = {
  label: 'live',
  className: 'bg-info-200',
  tooltip: 'Scale has approved this taxonomy',
};
const SPLIT_ROOT_COMMITTED_TAG: SidebarTag = {
  label: 'live',
  className: 'bg-info-200',
  tooltip: 'At least one subtaxonomy is committed',
};
const INTERNAL_TAG: SidebarTag = {
  label: 'scale',
  className: 'bg-neutral-100',
  tooltip: 'Scale ops defined taxonomy',
};
export const DRAFT_TAX_TAG: SidebarTag = {
  label: 'draft',
  className: 'bg-warning-200',
  tooltip: 'Taxonomy is editable',
};
export const DRAFT_INSTR_TAG: SidebarTag = {
  label: 'draft',
  className: 'bg-neutral-100',
  tooltip: 'Instructions is editable',
};

export function splitTaxonomyPropsToTag({
  isCustomerPublished,
  isCommitted,
  isInternal,
  isRoot,
}: {
  isCustomerPublished?: boolean;
  isCommitted?: boolean;
  isInternal?: boolean;
  isRoot?: boolean;
}) {
  if (isCommitted) {
    return isRoot ? SPLIT_ROOT_COMMITTED_TAG : SUB_COMMITTED_TAG;
  }
  if (isCustomerPublished) {
    return EXTERNAL_PUBLISHED_TAG;
  }
  if (isRoot) {
    return DRAFT_TAX_TAG;
  } else if (isInternal) {
    return INTERNAL_TAG;
  }
  return EXTERNAL_TAG;
}

const COMPOSABLE_INFO_MAP: PartialRecord<ComposableKey, { display: string; desc: string[] }> = {
  cuboid: {
    display: '3D Cuboids',
    desc: ['Labeling occurs in a 3D pointcloud viewer', 'Examples: Traffic Signals, Stop Signs'],
  },
  polygon_3d: {
    display: 'Planar 3D Polygons',
    desc: [
      'Uncommon annotation type. Annotations over road surfaces should use Topdown Polygon instead. Vertices must lie on the same plane. Labeling occurs in a 3D pointcloud viewer',
      'Examples: Stop Sign Face',
    ],
  },
  point_3d: {
    display: '3D Points',
    desc: ['Labeling occurs in a 3D pointcloud viewer', 'Examples: Pole'],
  },
  point_topdown: {
    display: 'Topdown Points',
    desc: [
      'Labeling occurs in a Topdown view of a filtered pointcloud. Use the "Dynamic 3D Point" type instead if the labeler may need a 3D context to annotate',
      'Examples: Lane Merge Point, Lane Split Point',
    ],
  },
  polyline_3d: {
    display: 'Topdown Polylines',
    desc: [
      'Labeling occurs in a Topdown view of a filtered pointcloud',
      'Examples: Lane Lines, Curbs',
    ],
  },
  polygon_topdown: {
    display: 'Topdown Polygons',
    desc: [
      'Also known as "Polygons with Elevation" or "Closed Polyline". Vertices do not necessarily lie on a plane. Labeling occurs in a Topdown view of a filtered pointcloud',
      'Examples: Parking Slot, Crosswalk, Lane Segment',
    ],
  },
  box_2d: {
    display: 'Camera/2D Boxes',
    desc: [
      'Labeling occurs in a camera image view. Receive annotations in image space',
      'Examples: Traffic Signals, Stop Signs',
    ],
  },
  point_2d: {
    display: 'Camera/2D Points',
    desc: ['Labeling occurs in a camera image view. Receive annotations in image space'],
  },
  polyline_2d: {
    display: 'Camera/2D Polylines',
    desc: [
      'Labeling occurs in a camera image view. Receive annotations in image space',
      'Examples: Lane Lines, Curbs',
    ],
  },
  polygon_2d: {
    display: 'Camera/2D Polygons',
    desc: [
      'Labeling occurs in a camera image view. Receive annotations in image space',
      'Examples: Crosswalks, Lane Segments',
    ],
  },
  links: {
    display: 'Links',
    desc: [
      'Capture relationships between geometries',
      'Examples: Road Sign To Lane Center, Lane Center To Lane Line, Traffic Light To Lane Center',
    ],
  },
  scene: {
    display: 'Scene / Global Attributes',
    desc: [
      'Attributes that apply to the whole scene rather than to a single annotation',
      'Examples: Is Blurry, Coverage Percent',
    ],
  },
  image_segmentation: {
    display: 'Image Segmentation',
    desc: [
      'Labeling occurs on a camera image',
      'Examples: Semantic Segmentation, Instance Segmentation, Panoptic Segmentation',
    ],
  },
  point_segmentation: {
    display: 'Point Segmentation',
    desc: ['Labeling occurs in a 3D pointcloud viewer', 'Examples: Ground Segmentation'],
  },
  event_2d: {
    display: 'Camera/2D Events',
    desc: [
      'Events are not linked to a specific object in the video, but rather to a frame/group of frames',
      'Examples: Car is turning left',
    ],
  },
  lidar_linking: {
    display: 'Lidar Linking',
    desc: ['Lidar Linking-specific taxonomy params', 'Examples: Annotation Type, Default Geometry'],
  },
  grid_segmentation: {
    display: 'Grid Segmentation',
    desc: ['Labeling occurs on a camera image', 'Examples: Grid Segmentation'],
  },
  cuboid_2d: {
    display: 'Camera/2D Cuboids',
    desc: [
      'Labeling occurs in a camera image view. Receive annotations in image space',
      'Examples: Vehicles',
    ],
  },
  group: {
    display: 'Group',
    desc: [
      'Create collections or annotations groups linked through "parent_id"',
      'Examples: Group of Cars, Group of Traffic Lights',
    ],
  },
  text_annotation: {
    display: 'Text Annotation',
    desc: [
      'Labeling occurs on 2D cameras. Annotate text fragments in time intervals, such as descriptions or captions.',
      'Examples: "Left gripper holds the plate"',
    ],
  },
};

export type ComposableInfoMap = typeof COMPOSABLE_INFO_MAP;

export function getFixedComposableInfoMap(taskType?: TaskType) {
  if (taskType === TaskType.SensorFusion) {
    return {
      ...COMPOSABLE_INFO_MAP,
      polyline_3d: {
        display: 'Polyline 3D',
        desc: [
          'Labeling occurs in a Topdown view of a filtered pointcloud',
          'Examples: Lane Lines, Curbs',
        ],
      },
    };
  }
  return COMPOSABLE_INFO_MAP;
}
export interface ComposableKeyInfo {
  key: ComposableKey;
  display: string;
  desc: string[];
  idx: number;
}

export enum Action {
  Addition = 'Additions',
  Deletion = 'Deletions',
  Rename = 'Renames',
}

export const ACTION_TO_TEXT_COLOR: Record<Action, string> = {
  [Action.Addition]: 'text-success-800',
  [Action.Deletion]: 'text-danger-800',
  [Action.Rename]: 'text-warning-800',
};

const ACTION_TO_HOVER_BUTTON_BACKGROUND_COLOR: Record<Action, string> = {
  [Action.Addition]: 'hover:bg-success-200',
  [Action.Deletion]: 'hover:bg-danger-200',
  [Action.Rename]: 'hover:bg-warning-300',
};

const ACTION_TO_BUTTON_BACKGROUND_COLOR: Record<Action, string> = {
  [Action.Addition]: 'bg-success-100',
  [Action.Deletion]: 'bg-danger-100',
  [Action.Rename]: 'bg-warning-100',
};

export function getButtonClasses(action: Action) {
  return twMerge(
    // ACTION_TO_TEXT_COLOR[action],
    ACTION_TO_BUTTON_BACKGROUND_COLOR[action],
    ACTION_TO_HOVER_BUTTON_BACKGROUND_COLOR[action],
  );
}

export const TAX_NAV_PAGINATION_LIMIT = 4;

export const NO_ATTRIBUTES_GEOMETRY_KEYS: GeometryKey[] = ['point_segmentation'];
